import { useSyncExternalStore } from "react";

import { isSupportAssistantEnabled } from "./supportAssistantAccess";

/**
 * ONE CONTEXT-SENSITIVE SEARCH FIELD ON THE JOB BOARD (2026-10-01 design review).
 *
 * The board used to show the plain search box (`GlobalSearch`) AND, stacked under it, CoSMo's
 * "describe what to show" box (`FilterAssist`), and nobody could tell which to use when. A
 * Smart|Basic switch was tried and dropped the same day: the user asked for ONE box that works
 * out what was meant. Where CoSMo can run, the field is `FilterAssist`, and on Enter it tries a
 * BASIC search first (text that matches a card — an order number, a stock code, a customer —
 * is searched exactly as the plain box would); only text that matches nothing goes to CoSMo.
 * Where CoSMo cannot run, the field is the plain box.
 *
 * The two boxes stay two components because they have different owners: `FilterAssist` must
 * write through `GlobalFilter`'s own setters (see the "week filter comes back checked but
 * unfiltered" entry in AGENTS.md), so `GlobalFilter` keeps rendering it and PORTALS it into a
 * slot `GlobalSearch` exposes where its plain input would otherwise be. This module is the
 * meeting point: the slot, and whether CoSMo is usable at all.
 *
 * - **The plain box is the fallback.** Where the assistant flag is off, or this region's
 *   Function App answered `503`/`404` (the box retired itself), the search row shows it.
 * - **No slot registered ⇒ `GlobalFilter` renders the box inline**, as it always did. That is
 *   the fallback for any host that mounts the bar without the search row.
 */

let slot: HTMLElement | null = null;
let retired = false;

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/** `GlobalSearch` registers the element the CoSMo box is portalled into (a ref callback). */
export function setSmartSearchSlot(el: HTMLElement | null): void {
  if (el === slot) return;
  slot = el;
  emit();
}

export function useSmartSearchSlot(): HTMLElement | null {
  return useSyncExternalStore(subscribe, () => slot);
}

/** `FilterAssist` calls this on `503`/`404`: CoSMo is not available here. */
export function retireSmartSearch(): void {
  if (retired) return;
  retired = true;
  emit();
}

/**
 * Lifts the retirement. `GlobalSearch` calls it on mount, so a region hop (which remounts the
 * board) asks the new Function App afresh — the same per-mount scope `FilterAssist`'s own
 * `unavailable` flag has always had.
 */
export function resetSmartSearchRetirement(): void {
  if (!retired) return;
  retired = false;
  emit();
}

/** Can the CoSMo box run here? The flag, and no `503`/`404` yet. */
export function useSmartSearchUsable(): boolean {
  const isRetired = useSyncExternalStore(subscribe, () => retired);
  return isSupportAssistantEnabled() && !isRetired;
}

/** Test seam. */
export function __resetSearchMode(): void {
  slot = null;
  retired = false;
  emit();
}
